import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api-client';
import { Plus, Pencil, Trash2, Eye, Search, PenSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';

const categories = ['Updates', 'Tutorials', 'Case Studies'];

export default function AdminBlog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const isSuperuser = user?.is_superuser || user?.is_admin || user?.is_staff;

  useEffect(() => {
    if (!isSuperuser) {
      navigate('/dashboard');
      return;
    }
    loadPosts();
  }, [isSuperuser, navigate]);

  const loadPosts = () => {
    setLoading(true);
    api.getBlogPosts({ admin: 'true' })
      .then(setPosts)
      .catch(err => console.error('Failed to load posts:', err))
      .finally(() => setLoading(false));
  };

  if (!isSuperuser) return null;

  const emptyPost = { title: '', slug: '', excerpt: '', category: 'Updates', readTime: '5 min', status: 'Draft', content: '' };

  const openNew = () => { setEditingPost({ ...emptyPost }); setEditorOpen(true); };
  const openEdit = (post) => { setEditingPost({ ...post }); setEditorOpen(true); };

  const savePost = async (status) => {
    if (!editingPost.title || !editingPost.excerpt) { 
      toast.error('Title and excerpt are required'); 
      return; 
    }
    
    setSaving(true);
    try {
      const postData = {
        ...editingPost,
        status,
        slug: editingPost.slug || editingPost.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      };
      
      if (editingPost.id) {
        await api.updateBlogPost(editingPost.id, postData);
        toast.success('Post updated');
      } else {
        await api.createBlogPost(postData);
        toast.success('Post created');
      }
      
      setEditorOpen(false);
      setEditingPost(null);
      loadPosts();
    } catch (err) {
      toast.error(err.message || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (id) => {
    try {
      await api.deleteBlogPost(id);
      setPosts(prev => prev.filter(p => p.id !== id));
      setDeleteConfirm(null);
      toast.success('Post deleted');
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const toggleStatus = async (post) => {
    try {
      const newStatus = post.status === 'Published' ? 'Draft' : 'Published';
      await api.updateBlogPost(post.id, { status: newStatus });
      setPosts(prev => prev.map(p => p.id === post.id ? { ...p, status: newStatus } : p));
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const filtered = posts.filter(p => !search || p.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="admin-blog" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <PenSquare className="text-emerald-500" size={24} />
          <div>
            <h1 className="text-2xl font-bold text-white font-mono">Blog Manager</h1>
            <p className="text-sm text-zinc-500">Create and manage blog posts (Superuser only)</p>
          </div>
        </div>
        <button data-testid="blog-new-post-btn" onClick={openNew} className="bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-5 py-2.5 hover:bg-emerald-400 transition-all click-scale flex items-center gap-2 shadow-[0_0_15px_rgba(16,185,129,0.3)]">
          <Plus size={16} /> New Post
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Posts', value: posts.length },
          { label: 'Published', value: posts.filter(p => p.status === 'Published').length },
          { label: 'Drafts', value: posts.filter(p => p.status === 'Draft').length },
        ].map((s, i) => (
          <div key={i} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-4">
            <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">{s.label}</span>
            <p className="text-2xl font-mono font-bold text-white mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input data-testid="blog-admin-search" type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search posts..." className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm pl-10 pr-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" />
      </div>

      {/* Posts Table */}
      <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Title', 'Category', 'Status', 'Date', 'Views', 'Actions'].map(h => (
                <th key={h} className="text-left py-3 px-4 text-xs font-mono text-zinc-500 uppercase tracking-widest">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3].map(i => (
                <tr key={i} className="border-b border-zinc-800/50">
                  <td colSpan={6} className="py-4 px-4">
                    <div className="h-4 bg-zinc-800 rounded animate-pulse"></div>
                  </td>
                </tr>
              ))
            ) : filtered.map(post => (
              <tr key={post.id} data-testid={`admin-post-${post.id}`} className="border-b border-zinc-800/50 hover:bg-white/[0.02]">
                <td className="py-3 px-4">
                  <p className="text-sm font-mono text-white">{post.title}</p>
                  <p className="text-xs text-zinc-600 font-mono mt-0.5">{post.slug}</p>
                </td>
                <td className="py-3 px-4">
                  <span className="text-xs font-mono text-zinc-400 bg-zinc-800/50 px-2 py-0.5 rounded-sm">{post.category}</span>
                </td>
                <td className="py-3 px-4">
                  <button data-testid={`toggle-status-${post.id}`} onClick={() => toggleStatus(post)} className={`text-xs font-mono px-2 py-0.5 rounded-sm cursor-pointer transition-all ${post.status === 'Published' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'}`}>
                    {post.status}
                  </button>
                </td>
                <td className="py-3 px-4 text-sm font-mono text-zinc-400">{post.created_at?.slice(0, 10) || '-'}</td>
                <td className="py-3 px-4 text-sm font-mono text-zinc-400">{post.views?.toLocaleString() || 0}</td>
                <td className="py-3 px-4">
                  <div className="flex gap-1">
                    <Link to={`/blog/${post.slug}`} data-testid={`view-post-${post.id}`} className="p-1.5 rounded-sm hover:bg-emerald-500/10 text-zinc-500 hover:text-emerald-400 transition-all"><Eye size={14} /></Link>
                    <button data-testid={`edit-post-${post.id}`} onClick={() => openEdit(post)} className="p-1.5 rounded-sm hover:bg-cyan-500/10 text-zinc-500 hover:text-cyan-400 transition-all"><Pencil size={14} /></button>
                    <button data-testid={`delete-post-${post.id}`} onClick={() => setDeleteConfirm(post.id)} className="p-1.5 rounded-sm hover:bg-red-500/10 text-zinc-500 hover:text-red-400 transition-all"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={() => { setEditorOpen(false); setEditingPost(null); }}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-mono">{editingPost?.id ? 'Edit Post' : 'New Post'}</DialogTitle>
            <DialogDescription className="text-zinc-500 font-mono text-xs">Fill in the details below</DialogDescription>
          </DialogHeader>
          {editingPost && (
            <div className="space-y-4 mt-2">
              <div>
                <label className="block text-xs font-mono text-zinc-500 mb-1.5">Title *</label>
                <input data-testid="blog-editor-title" type="text" value={editingPost.title} onChange={e => setEditingPost({ ...editingPost, title: e.target.value })} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" placeholder="Post title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-mono text-zinc-500 mb-1.5">Category</label>
                  <select data-testid="blog-editor-category" value={editingPost.category} onChange={e => setEditingPost({ ...editingPost, category: e.target.value })} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none">
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-mono text-zinc-500 mb-1.5">Read Time</label>
                  <input type="text" value={editingPost.readTime || ''} onChange={e => setEditingPost({ ...editingPost, readTime: e.target.value })} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" placeholder="5 min" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono text-zinc-500 mb-1.5">Excerpt *</label>
                <input data-testid="blog-editor-excerpt" type="text" value={editingPost.excerpt} onChange={e => setEditingPost({ ...editingPost, excerpt: e.target.value })} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" placeholder="Brief description" />
              </div>
              <div>
                <label className="block text-xs font-mono text-zinc-500 mb-1.5">Content (Markdown)</label>
                <textarea data-testid="blog-editor-content" rows={12} value={editingPost.content || ''} onChange={e => setEditingPost({ ...editingPost, content: e.target.value })} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600 resize-none" placeholder="Write your article in markdown..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => { setEditorOpen(false); setEditingPost(null); }} className="flex-1 border border-zinc-800 text-zinc-400 font-mono text-sm rounded-sm py-2.5 hover:border-zinc-700 transition-all">Cancel</button>
                <button onClick={() => savePost('Draft')} disabled={saving} className="border border-zinc-800 text-zinc-400 font-mono text-sm rounded-sm px-5 py-2.5 hover:border-zinc-700 transition-all disabled:opacity-50">Save as Draft</button>
                <button data-testid="blog-editor-publish" onClick={() => savePost('Published')} disabled={saving} className="flex-1 bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm py-2.5 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_15px_rgba(16,185,129,0.3)] disabled:opacity-50">
                  {saving ? 'Publishing...' : 'Publish'}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white font-mono">Delete Post</DialogTitle>
            <DialogDescription className="text-zinc-500 font-mono text-xs">This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setDeleteConfirm(null)} className="flex-1 border border-zinc-800 text-zinc-400 font-mono text-sm rounded-sm py-2.5 hover:border-zinc-700 transition-all">Cancel</button>
            <button data-testid="confirm-delete-post" onClick={() => deletePost(deleteConfirm)} className="flex-1 bg-red-500 text-white font-mono font-bold text-sm rounded-sm py-2.5 hover:bg-red-400 transition-all">Delete</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
